/**
 * useComplianceStrip — compliance indicator hook (RAISE-16886 T6).
 *
 * Composes useWorkState + useAuditEvents to derive ComplianceRow[] for
 * stories active in the last 30 days. Each row has three boolean indicators:
 * gates, arAttest, retro. 403 on either source → planGated: true.
 */

import { useMemo, useState } from "react";
import { useWorkState } from "@/hooks/useWorkState";
import { useAuditEvents } from "@/hooks/useAuditEvents";
import { useProjects } from "@/hooks/useProjects";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ComplianceRow {
  storyKey: string;
  gates: boolean;
  arAttest: boolean;
  retro: boolean;
}

export interface ComplianceStripResult {
  rows: ComplianceRow[];
  loading: boolean;
  planGated: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const LIMIT = 50;

const GATE_PASS_DECISIONS = new Set(["pass", "approve", "auto_approve"]);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useComplianceStrip(projectSlug: string): ComplianceStripResult {
  // Captured once at mount time via lazy useState initializer.
  // The lazy init function runs before the first render, outside the render
  // cycle, so it is not flagged by react-hooks/purity.
  const [since] = useState((): string => {
    const d = new Date();
    d.setTime(d.getTime() - THIRTY_DAYS_MS);
    return d.toISOString();
  });

  // Resolve project ID from slug (needed for audit endpoint)
  const { projects, loading: projectsLoading } = useProjects();
  const projectId =
    projects.find((p) => p.slug === projectSlug)?.id ?? null;

  const {
    state: workState,
    loading: workLoading,
    error: workError,
  } = useWorkState(projectSlug, { since, limit: LIMIT });

  const { events, loading: auditLoading, forbidden } = useAuditEvents(
    projectId ? { projectId, since } : {},
    { enabled: !!projectId },
  );

  const loading = projectsLoading || workLoading || auditLoading;

  return useMemo(() => {
    if (loading) {
      return { rows: [], loading: true, planGated: false };
    }

    // A 403/500 on workState makes `runs` empty — surface as planGated to
    // avoid a misleading "No stories in the last 30 days" message.
    const planGated = forbidden || workError !== null;

    const runs = workState?.pipeline_runs ?? [];

    // Deduplicate by issue_id — one row per story
    const uniqueIssueIds = Array.from(
      new Set(
        runs
          .map((r) => r.issue_id)
          .filter((id): id is string => id !== null),
      ),
    );

    if (uniqueIssueIds.length === 0) {
      return { rows: [], loading: false, planGated };
    }

    // Index events by work_item_ref for O(n) lookup
    const eventsByRef = new Map<string, typeof events>();
    for (const evt of events) {
      if (evt.work_item_ref) {
        const existing = eventsByRef.get(evt.work_item_ref) ?? [];
        existing.push(evt);
        eventsByRef.set(evt.work_item_ref, existing);
      }
    }

    const rows: ComplianceRow[] = uniqueIssueIds.map((issueId) => {
      const storyEvents = eventsByRef.get(issueId) ?? [];

      // Gates: any gate_decision event with a pass-family decision
      const gates = storyEvents.some(
        (e) =>
          e.event_kind === "gate_decision" &&
          e.decision !== null &&
          GATE_PASS_DECISIONS.has(e.decision),
      );

      // AR-Attest: any event where subject_id includes "review"
      const arAttest = storyEvents.some((e) =>
        e.subject_id.toLowerCase().includes("review"),
      );

      // Retro: any event where subject_id includes "close" or "retro"
      const retro = storyEvents.some(
        (e) =>
          e.subject_id.toLowerCase().includes("close") ||
          e.subject_id.toLowerCase().includes("retro"),
      );

      return { storyKey: issueId, gates, arAttest, retro };
    });

    return { rows, loading: false, planGated };
  }, [loading, workState, workError, events, forbidden]);
}
